import { StrategyCode } from '#/shared/strategies';
import { PRODUCT_CODES } from './config';

export type ContractType =
  // Options
  | 'EUROPEAN'
  // Futures
  | 'INVERSE'
  | 'LINEAR';

export type MarginType = 'LINEAR' | 'INVERSE';
// --- BEGIN GENERATED PRODUCT CONFIG (paradex-venues) ---
export type VenueCode = 'BIT' | 'DBT' | 'BYB' | 'PRDX' | 'BLSH';
// --- END GENERATED PRODUCT CONFIG (paradex-venues) ---

/**
 * Most of products follow a standard specification that contains a `minBlockSize`
 * for calculating minimum quantities
 */
export interface StandardProduct<Code extends string = string> {
  readonly code: Code;
  readonly venue: VenueCode;
  readonly kind: 'OPTION' | 'FUTURE';
  readonly currency: string;
  readonly contractType: ContractType;
  /**
   * For option legs only.
   * Used when hedge contract type is different from option contract type.
   * E.g: Bitcom BD is EUROPEAN, but Bitcom PE is LINEAR
   **/
  readonly marginType: MarginType;
  readonly shortName: string;
  readonly longName: string;
  /** Contract Multiplier */
  readonly contractSize: number;
  /**
   * Contract size for DATED instruments, when it differs from the perpetual
   * `contractSize` (Deribit linear futures: SOL 0.1/0.01, XRP 10/1; #86).
   * Mirrors the backend blocksize `dated_contract_size` column. Absent => the
   * dated and perpetual contract sizes are the same (e.g. HYPE 0.1).
   */
  readonly datedContractSize?: number;
  readonly contractCurrency: string;
  readonly minBlockSize: number;
  readonly strategies: 'all' | readonly StrategyCode[];
  readonly quantityPrecision: number;
  readonly pricePrecision: number;
  readonly ratioPrecision: number;
  readonly minContractIncrement: number;
  readonly isHedgeDisabled?: boolean;
  /**
   * The product's perpetual is hedge-only (the option hedge IS the
   * perpetual), so it must not be selectable as a standalone strategy
   * future leg. Standalone-tradable perps (e.g. Paradex) leave this unset.
   */
  readonly isPerpStrategyLegDisabled?: boolean;
  readonly showUSDApproximation?: boolean;
  readonly isNew?: boolean;
  readonly isDisabled?: boolean;
  readonly isDisabledForOb?: boolean;
  readonly isDisabledForRfq?: boolean;
  readonly quoteCurrency?: string;
}

/**
 * Product were extracted from the spreadsheet bellow
 *   https://docs.google.com/spreadsheets/d/1wI7t38ES0f-CQTwX9MKm-r9VAPGXsov28EjzFNMGxIQ/edit#gid=1365784445
 */
export type Product<Code extends string = string> = StandardProduct<Code>;

export type ProductCode = typeof PRODUCT_CODES[number];
export interface StandardProductWithSpot<Code extends string = string>
  extends Omit<StandardProduct<Code>, 'kind'> {
  readonly kind: 'OPTION' | 'FUTURE' | 'SPOT';
}

export type ProductWithSpot<Code extends string = string> =
  StandardProductWithSpot<Code>;

export const isStandardProduct = (
  product: Product,
): product is StandardProduct => 'minBlockSize' in product;

export const isProductDisabledForType = (
  product: ProductWithSpot,
  type: 'OB' | 'RFQ',
) => {
  if (type === 'OB') return product.isDisabledForOb === true;
  return product.isDisabledForRfq === true;
};
